import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Calendar, MapPin, Users, Plus, X, Image, Video, Trash2, Edit2, Bell, Share2, Search, Link2, UserPlus, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const { user, token } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', event_type: 'meetup',
    date: '', time: '', location: { city: '', country: '', venue: '', address: '' },
    max_attendees: '', image_url: ''
  });
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events`, { headers });
      setEvents(res.data);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 3) { toast.error('Max 3 files'); return; }
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  const resetForm = () => {
    setFormData({ title: '', description: '', event_type: 'meetup', date: '', time: '', location: { city: '', country: '', venue: '', address: '' }, max_attendees: '', image_url: '' });
    setSelectedFiles([]);
    setEditEvent(null);
  };

  const createOrUpdateEvent = async () => {
    if (!formData.title || !formData.date || !formData.time) {
      toast.error('Title, date, and time are required');
      return;
    }
    setUploadingMedia(true);
    try {
      let imageUrl = formData.image_url;
      if (selectedFiles.length > 0 && !editEvent) {
        const fd = new FormData();
        selectedFiles.forEach(f => fd.append('files', f));
        const uploadRes = await axios.post(`${API}/upload/media`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        if (uploadRes.data.length > 0) imageUrl = uploadRes.data[0].url;
      }
      const payload = {
        ...formData,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        image_url: imageUrl
      };
      if (editEvent) {
        await axios.put(`${API}/events/${editEvent.id}`, payload, { headers });
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/events/create`, payload, { headers });
        toast.success('Event created!');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally { setUploadingMedia(false); }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers });
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const joinEvent = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/join`, {}, { headers });
      toast.success('Joined event!');
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const leaveEvent = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/leave`, {}, { headers });
      toast.success('Left event');
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to leave'); }
  };

  const openEdit = (event) => {
    setEditEvent(event);
    setFormData({
      title: event.title, description: event.description, event_type: event.event_type,
      date: event.date, time: event.time, location: event.location || { city: '', country: '', venue: '', address: '' },
      max_attendees: event.max_attendees || '', image_url: event.image_url || ''
    });
    setShowCreateDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-coral" />
            <span>Events</span>
          </h1>
          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-event-btn" className="bg-coral text-white rounded-full px-6">
                <Plus className="w-4 h-4 mr-2" /> Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div><Label>Title *</Label><Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Event title" className="rounded-xl" /></div>
                <div><Label>Description</Label><Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="What's the event about?" className="rounded-xl" rows={3} /></div>
                <div>
                  <Label>Event Type</Label>
                  <Select value={formData.event_type} onValueChange={v => setFormData({ ...formData, event_type: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['meetup', 'party', 'outdoor', 'conference', 'workshop', 'dining'].map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date *</Label><Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} className="rounded-xl" /></div>
                  <div><Label>Time *</Label><Input type="time" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>City</Label><Input value={formData.location.city} onChange={e => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })} className="rounded-xl" /></div>
                  <div><Label>Country</Label><Input value={formData.location.country} onChange={e => setFormData({ ...formData, location: { ...formData.location, country: e.target.value } })} className="rounded-xl" /></div>
                </div>
                <div><Label>Max Attendees</Label><Input type="number" min="1" max="100" value={formData.max_attendees} onChange={e => { const v = e.target.value; if (v === '' || (parseInt(v) >= 1 && parseInt(v) <= 100)) setFormData({ ...formData, max_attendees: v }); }} placeholder="Leave empty for unlimited" className="rounded-xl" /></div>
                {!editEvent && (
                  <div>
                    <Label>Event Media</Label>
                    {selectedFiles.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-2 mt-2">
                        {selectedFiles.map((f, i) => (
                          <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-gray-200">
                            {f.type.startsWith('image/') ? <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" /> : <Video className="w-8 h-8 text-gray-400 m-auto" />}
                            <button onClick={() => removeFile(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input type="file" id="event-media" accept="image/*,video/*" multiple onChange={handleFileSelect} className="hidden" />
                    <label htmlFor="event-media" className="cursor-pointer inline-flex items-center px-4 py-2 rounded-xl bg-white border-2 border-teal text-sm font-semibold hover:bg-teal hover:text-white">
                      <Image className="w-4 h-4 mr-2" />{selectedFiles.length > 0 ? `${selectedFiles.length}/3` : 'Add Photos'}
                    </label>
                  </div>
                )}
                <Button data-testid="submit-event-btn" onClick={createOrUpdateEvent} disabled={uploadingMedia} className="w-full bg-coral text-white rounded-xl py-6">
                  {uploadingMedia ? 'Saving...' : editEvent ? 'Update Event' : 'Create Event'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto" /></div>
          ) : events.length === 0 ? (
            <div className="col-span-full glass rounded-3xl p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No events yet. Be the first to create one!</p>
            </div>
          ) : (
            events.map(event => (
              <EventCard
                key={event.id}
                event={event}
                currentUserId={user.id}
                token={token}
                onJoin={joinEvent}
                onLeave={leaveEvent}
                onEdit={openEdit}
                onDelete={deleteEvent}
                onRefresh={fetchEvents}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event, currentUserId, token, onJoin, onLeave, onEdit, onDelete, onRefresh }) => {
  const navigate = useNavigate();
  const isAttending = event.attendees.includes(currentUserId);
  const isCreator = event.creator_id === currentUserId;
  const isFull = event.max_attendees && event.attendees.length >= event.max_attendees;
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const typeColors = {
    meetup: 'bg-blue-100 text-blue-700', party: 'bg-purple-100 text-purple-700',
    outdoor: 'bg-green-100 text-green-700', conference: 'bg-orange-100 text-orange-700',
    workshop: 'bg-pink-100 text-pink-700', dining: 'bg-yellow-100 text-yellow-700'
  };

  const searchUsers = async (q) => {
    setInviteSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/users/discover?name=${q}`, { headers });
      setSearchResults(res.data.filter(u => !event.attendees.includes(u.id)));
    } catch { setSearchResults([]); }
  };

  const inviteUser = async (userId) => {
    try {
      await axios.post(`${API}/events/${event.id}/invite`, { user_id: userId }, { headers });
      toast.success('Invitation sent!');
      setSearchResults(prev => prev.filter(u => u.id !== userId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to invite'); }
  };

  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/send-reminder`, {}, { headers });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reminder'); }
    finally { setSendingReminder(false); }
  };

  const shareLink = () => {
    const url = `${window.location.origin}/events`;
    navigator.clipboard.writeText(url);
    toast.success('Event link copied to clipboard!');
  };

  return (
    <div className="glass-dark rounded-3xl overflow-hidden shadow-lg" data-testid={`event-${event.id}`}>
      {event.image_url && <img src={event.image_url} alt={event.title} className="w-full h-48 object-cover" loading="lazy" />}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <Badge className={`${typeColors[event.event_type] || 'bg-gray-100 text-gray-700'} border-none`}>{event.event_type}</Badge>
          {isCreator && <Badge className="bg-gold text-white border-none">Creator</Badge>}
        </div>
        <h3 className="text-xl font-bold mb-2">{event.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-gray-600 text-sm"><Calendar className="w-4 h-4 mr-2" />{new Date(event.date).toLocaleDateString()} at {event.time}</div>
          <div className="flex items-center text-gray-600 text-sm"><MapPin className="w-4 h-4 mr-2" />{event.location?.city || 'TBD'}{event.location?.country ? `, ${event.location.country}` : ''}</div>
          <div className="flex items-center text-gray-600 text-sm"><Users className="w-4 h-4 mr-2" />{event.attendees.length} attending{event.max_attendees ? ` / ${event.max_attendees}` : ''}</div>
        </div>

        {/* Creator actions */}
        {isCreator && (
          <div className="space-y-2 mb-3">
            <div className="flex space-x-2">
              <Button data-testid={`send-reminder-${event.id}`} onClick={sendReminder} disabled={sendingReminder || event.attendees.length <= 1} size="sm" className="flex-1 bg-teal text-white rounded-full text-xs">
                <Bell className="w-3 h-3 mr-1" />{sendingReminder ? 'Sending...' : 'Send Reminder'}
              </Button>
              <Button data-testid={`share-link-${event.id}`} onClick={shareLink} size="sm" variant="outline" className="rounded-full border-teal text-teal text-xs">
                <Link2 className="w-3 h-3 mr-1" />Share
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => setShowInvite(!showInvite)} size="sm" variant="outline" className="flex-1 rounded-full border-coral text-coral text-xs">
                <UserPlus className="w-3 h-3 mr-1" />Invite
              </Button>
              <Button onClick={() => onEdit(event)} size="sm" variant="outline" className="rounded-full text-xs">
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button onClick={() => onDelete(event.id)} size="sm" variant="outline" className="rounded-full text-red-500 border-red-200 text-xs">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>

            {/* Invite panel */}
            {showInvite && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 mt-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <Input
                    data-testid={`invite-search-${event.id}`}
                    value={inviteSearch}
                    onChange={e => searchUsers(e.target.value)}
                    placeholder="Search users to invite..."
                    className="pl-7 h-8 text-xs rounded-lg"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {searchResults.slice(0, 5).map(u => (
                      <div key={u.id} className="flex items-center justify-between p-1.5 rounded-lg hover:bg-gray-50">
                        <span className="text-xs font-medium truncate">{u.name}</span>
                        <Button size="sm" onClick={() => inviteUser(u.id)} className="h-6 text-xs bg-coral text-white rounded-full px-3">Invite</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Join/Leave for non-creators */}
        {!isCreator && (
          <Button
            data-testid={`event-action-btn-${event.id}`}
            onClick={() => isAttending ? onLeave(event.id) : onJoin(event.id)}
            disabled={!isAttending && isFull}
            className={`w-full rounded-full font-semibold ${
              isAttending ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-white text-black border-2 border-teal hover:bg-teal hover:text-white'
            }`}
          >
            {isAttending ? 'Leave Event' : isFull ? 'Event Full' : 'Join Event'}
          </Button>
        )}

        {/* Group Call button for attendees */}
        {isAttending && (
          <Button
            data-testid={`event-call-btn-${event.id}`}
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/events/${event.id}/start-call`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                navigate(`/group-calls?room=${res.data.id}`);
              } catch (err) {
                toast.error('Failed to start call');
              }
            }}
            className="w-full rounded-full font-semibold bg-coral text-white hover:bg-coral/90 mt-2"
          >
            <Phone className="w-4 h-4 mr-2" /> Join Group Call
          </Button>
        )}
      </div>
    </div>
  );
};

export default Events;
